import React, { useEffect, useRef, useState } from "react";
import { appleId, appleRedirectUri } from "@/config/app";
import AppleImageSrc from "@/images/icons/apple.svg";

const APPLE_JS =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

declare global {
  interface Window {
    AppleID: any;
  }
}

const AppleSignInBtn = ({
  socialCallback,
  buttonTitle,
  className = "apple"
}: {
  socialCallback: any;
  buttonTitle?: string;
  className?: string;
}) => {
  const [ready, setReady] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const initedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const tryInit = () => {
      if (initedRef.current) return;
      if (window.AppleID?.auth?.init) {
        window.AppleID.auth.init({
          clientId: appleId,
          scope: "name email",
          redirectURI: appleRedirectUri,
          state: "Initial user authentication request",
          usePopup: true
        });
        initedRef.current = true;
        setReady(true);
      }
    };

    if (window.AppleID?.auth) {
      tryInit();
      return;
    }

    let s = document.querySelector(
      'script[data-appleid="auth"]'
    ) as HTMLScriptElement | null;
    if (!s) {
      s = document.createElement("script");
      s.src = APPLE_JS;
      s.async = true;
      s.defer = true;
      s.dataset.appleid = "auth";
      document.body.appendChild(s);
    }
    s.addEventListener("load", tryInit, { once: true });
    s.addEventListener("error", () => setReady(false), { once: true });
  }, []);

  const handleClick = async () => {
    if (!ready || signingIn) return;
    try {
      setSigningIn(true);

      const data = await window.AppleID.auth.signIn();
      await socialCallback(data);
    } catch (e) {
      console.error("Apple signIn error:", e);
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={className}
      disabled={!ready || signingIn}
      aria-disabled={!ready || signingIn}
    >
      {buttonTitle ? buttonTitle : <img src={AppleImageSrc} alt="apple" />}
    </button>
  );
};

export default AppleSignInBtn;
